'use client';

import { Children, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductStripProps {
  children: ReactNode;
  /** Texto accesible del botón de retroceso. */
  prevLabel: string;
  /** Texto accesible del botón de avance. */
  nextLabel: string;
}

/**
 * Tira horizontal de tarjetas de producto (ver `.product-strip` en globals.css).
 *
 * En pantallas anchas caben las 6 que define `--product-card` y el resto se
 * alcanza con las flechas; cuando no desborda, las flechas no se pintan. En
 * móvil se desliza con el dedo y se ocultan (un botón de 36px sobre una tira que
 * ya responde al gesto solo tapa producto).
 */
export default function ProductStrip({ children, prevLabel, nextLabel }: ProductStripProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // El nº de tarjetas es lo que hace cambiar el `scrollWidth`, así que sirve de
  // disparador para recalcular cuando la sección termina de cargar.
  const cardCount = Children.count(children);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;

    const sync = () => {
      const maxScroll = strip.scrollWidth - strip.clientWidth;
      // Margen de 4px: los redondeos a subpíxel del ancho de tarjeta dejan
      // sobrantes de <1px que harían aparecer las flechas sin nada que desplazar.
      setOverflowing(maxScroll > 4);
      setAtStart(strip.scrollLeft <= 2);
      setAtEnd(strip.scrollLeft >= maxScroll - 2);
    };

    // Contenido nuevo (cambio de filtro): se vuelve al principio de la tira.
    strip.scrollLeft = 0;
    sync();

    strip.addEventListener('scroll', sync, { passive: true });
    // ResizeObserver en vez de `resize` de window: la tira también cambia de
    // ancho al abrirse/cerrarse la barra de scroll vertical de la página.
    const observer = new ResizeObserver(sync);
    observer.observe(strip);

    return () => {
      strip.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, [cardCount]);

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const strip = stripRef.current;
    if (!strip) return;
    // 80% del ancho visible: deja una tarjeta de solape como pista de contexto.
    strip.scrollBy({ left: direction * strip.clientWidth * 0.8 });
  }, []);

  const arrowClass =
    'absolute top-[38%] z-10 hidden h-9 w-9 place-items-center rounded-full border border-border ' +
    'bg-card/95 text-foreground shadow-md transition-colors hover:border-muted-foreground ' +
    'disabled:cursor-default disabled:opacity-30 sm:grid';

  return (
    <div className="product-track relative">
      {overflowing ? (
        <button
          type="button"
          aria-label={prevLabel}
          disabled={atStart}
          onClick={() => scrollByPage(-1)}
          className={`${arrowClass} -left-3.5`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      ) : null}

      <div ref={stripRef} className="product-strip">
        {children}
      </div>

      {overflowing ? (
        <button
          type="button"
          aria-label={nextLabel}
          disabled={atEnd}
          onClick={() => scrollByPage(1)}
          className={`${arrowClass} -right-3.5`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
